import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthLoadingView } from 'shared-ui/lib/components/auth/AuthLoadingView';
import { GenericFormScene } from 'shared-ui/lib/components/generic-form-scene/GenericFormScene';
import { parseGraphqlErrors } from 'shared-ui/lib/utils/get-gql-error';
import { GraphQLError } from 'graphql';
import { FaultSuccessModal } from '../components/FaultSuccessModal';
import { FaultFormBody, useFaultForm } from '../form/fault/fault.form';
import { useCreateFaultMutation } from '../generated/graphql';
import { useUploadFile } from '../hooks/use-upload-file';

export const HomeScene = (): JSX.Element => {
  const [key, setKey] = useState(1);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const { t } = useTranslation('HomeScene');
  const { form, loading: isVehiclesLoading, hasVehicles } = useFaultForm();

  const [createFault, { loading }] = useCreateFaultMutation();
  const { uploadFile, isImageLoading } = useUploadFile({
    type: 'fault-image',
  });

  const handleSubmit = async ({ photo, ...body }: FaultFormBody) => {
    const { data, errors } = await createFault({ variables: { body } });
    if (!data) throw new Error(parseGraphqlErrors(errors as GraphQLError[]));
    await uploadFile(photo, data.createFault.id);
    setIsSuccessModalOpen(true);
    setKey(key + 1);
  };

  if (isVehiclesLoading) return <AuthLoadingView />;

  if (!hasVehicles) {
    return (
      <div className="mx-auto w-full max-w-3xl sm:px-2">
        <div className="bg-white py-8 px-6 sm:px-10 shadow rounded-lg">
          <h2 className="text-lg leading-6 font-bold text-primary">{t('title')}</h2>
          <p className="mt-4 text-sm font-medium text-gray-color">{t('noVisibleVehicles')}</p>
        </div>
      </div>
    );
  }

  if (!form) return <AuthLoadingView />;

  return (
    <>
      <div key={key}>
        <GenericFormScene
          type="create"
          title={t('title')}
          onSubmit={handleSubmit}
          form={form}
          isSubmitLoading={loading || isImageLoading}
        />
      </div>

      {isSuccessModalOpen ? (
        <FaultSuccessModal onClose={() => setIsSuccessModalOpen(false)} />
      ) : null}
    </>
  );
};
